import React, { useEffect, useState } from "react";
import {
  NavLink,
  useLoaderData,
  useLocation,
  useSearchParams,
} from "react-router";
import { Icon } from "~/components/ui/icon";
import { UserDropdown } from "~/components/features/user-dropdown";
import { ThemeSwitch } from "~/routes/resources+/theme-switch";
import { cn } from "~/lib/utils";
import { loader } from "~/root";
import { LinkType } from "~/lib/utils/nav-links";
import { Avatar, AvatarImage } from "../ui/avatar";
import { TenantLink } from "~/components/tenant-link";
import { TeamRoles } from "~/lib/utils/roles";

const dummyLinks = [
  { name: "Inicio", to: "/", iconName: "house", teamRoles: [TeamRoles.VIEWER] },
] satisfies LinkType[];

export const TeamOnboardingLayout = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [teamLogoPreview, setTeamLogoPreview] = useState<string | null>(() =>
    typeof window !== "undefined"
      ? sessionStorage.getItem("teamLogoPreview")
      : null
  );

  const data = useLoaderData<typeof loader>();
  const location = useLocation();

  const [searchParams] = useSearchParams();
  const teamName = searchParams.get("teamName") || "";
  const teamMembers = searchParams.getAll("teamMember") || [];

  useEffect(() => {
    // Handler for the custom event
    const handlePreviewChange = (event: CustomEvent<string>) => {
      setTeamLogoPreview(event.detail);
    };

    // Add event listener
    window.addEventListener(
      "teamLogoPreviewChange",
      handlePreviewChange as EventListener
    );

    // Cleanup function
    return () => {
      window.removeEventListener(
        "teamLogoPreviewChange",
        handlePreviewChange as EventListener
      );
      if (window.location.pathname !== "/team-onboarding") {
        sessionStorage.removeItem("teamLogoPreview");
        setTeamLogoPreview(null);
      }
    };
  }, []);

  return (
    <div className="relative grid grid-cols-[auto] grid-rows-[var(--header-height)_auto] md:grid-cols-[var(--sidebar-width)_auto_8px] h-dvh w-full bg-accent overflow-hidden">
      {/* Header */}
      <header className="z-[500] col-[1/-1] row-[1/2] md:col-[1/4]">
        <nav className="relative flex items-center justify-between h-full gap-10 px-5">
          <div className="flex items-center gap-10">
            <TenantLink
              to="/"
              className="flex items-center h-full gap-3 font-black cursor-pointer ml-12 md:ml-0"
            >
              {teamLogoPreview && (
                <img
                  src={teamLogoPreview}
                  alt={teamName.toUpperCase()}
                  className="h-[35px] dark:filter dark:grayscale dark:brightness-0 dark:invert"
                />
              )}

              {teamName.toUpperCase()}
            </TenantLink>
          </div>

          <div className="flex items-center gap-4 text-xl">
            <ThemeSwitch userPreference={data.requestInfo.userPrefs.theme} />
            <UserDropdown />
          </div>
        </nav>
      </header>

      {/* Desktop Sidebar */}
      <aside className="hidden md:block col-[1/2] row-[2/3] pt-5">
        <nav role="navigation">
          <ul className="flex flex-col items-center justify-center gap-3">
            {dummyLinks.map(({ to, name, iconName }) => {
              const isHome = location.pathname === "/";
              const isActive = isHome
                ? location.pathname === to
                : location.pathname.startsWith(to) && to !== "/";

              return (
                <li key={to} className="flex flex-col justify-center">
                  <NavLink
                    to={to}
                    className="group flex flex-col gap-1 items-center justify-center text-center text-[10px] leading-3 leading max-w-10"
                    viewTransition
                  >
                    <span
                      className={cn(
                        "rounded-lg p-1.5 transition-colors duration-200",
                        {
                          "text-background bg-foreground": isActive,
                          "group-hover:bg-foreground/10": !isActive,
                        }
                      )}
                    >
                      <Icon
                        name={iconName}
                        className="w-sidebar-icon-height h-sidebar-icon-height"
                      />
                    </span>
                    {name}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>

      {/* Main Content */}
      <div className="col-[1/-1] row-[2/3] md:col-[2/3] md:row-[2/3] h-full md:rounded-t-xl bg-background">
        <div className="grid grid-cols-[1fr_4fr]">
          {/* Team invitees */}
          <div className="flex flex-col p-6 gap-6">
            {teamName && <h3 className="text-h2">{teamName}</h3>}

            {teamMembers.length > 0 && (
              <div className="flex flex-col gap-2">
                <h4 className="font-semibold">Equipo</h4>
                <ul className="flex flex-col gap-2">
                  {teamMembers.map((member) => (
                    <li
                      key={member}
                      className="flex items-center gap-1.5 text-sm text-muted-foreground"
                    >
                      <Avatar className="w-5 h-5 rounded-sm">
                        <AvatarImage
                          src="/assets/images/fallback-user.png"
                          alt={`${member} image`}
                          className="invert p-1 bg-muted-foreground/20"
                        />
                      </Avatar>
                      {member}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Team form */}
          {children}
        </div>
      </div>
    </div>
  );
};
